import type { Assignment } from "@/types";

/**
 * Client-side cohort schedule helpers (KC-363).
 *
 * Mirrors the backend `services/scheduling.py`: a cohort schedule is a list of
 * assignment windows; week/assignment numbers are derived (contiguous,
 * Monday-aligned) and the work-package number is the global chronological
 * sequence (1..N) that replaces the old `(week-1)*2 + assignment`.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export interface ScheduleWindow {
  start: Date;
  deadline: Date;
}

export interface NumberedRow extends ScheduleWindow {
  week: number; // contiguous, Monday-aligned (1-based)
  assignment: number; // order within the week (1-based)
  wp: number; // global work-package number (1..N)
}

/** Monday (local) of the week containing `d`, at midnight. */
function mondayOf(d: Date): number {
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
  return monday.getTime();
}

/** Order a set of assignment windows chronologically by start (then deadline). */
export function orderWindows<T extends ScheduleWindow>(windows: T[]): T[] {
  return [...windows].sort(
    (a, b) => a.start.getTime() - b.start.getTime() || a.deadline.getTime() - b.deadline.getTime()
  );
}

/** Derive week / assignment / work-package numbers for an ordered window list. */
export function numberWindows(windows: ScheduleWindow[]): NumberedRow[] {
  const ordered = orderWindows(windows);
  const rows: NumberedRow[] = [];
  let week = 0;
  let assignment = 0;
  let prevMonday: number | null = null;
  ordered.forEach((w, index) => {
    const monday = mondayOf(w.start);
    if (monday !== prevMonday) {
      week += 1;
      assignment = 0;
      prevMonday = monday;
    }
    assignment += 1;
    rows.push({ ...w, week, assignment, wp: index + 1 });
  });
  return rows;
}

/**
 * Seed `count` back-to-back windows from `start` (mirrors the backend seed):
 * pack floor(7 / durationDays) per Monday-aligned week, dropping slots that
 * open before `start` (partial first week).
 */
export function seedSchedule(start: Date, durationDays: number, count: number): ScheduleWindow[] {
  const windows: ScheduleWindow[] = [];
  const perWeek = Math.floor(7 / durationDays);
  const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS);

  if (perWeek >= 1) {
    const startDay = (start.getDay() + 6) % 7; // 0 = Monday
    const anchorMonday = addDays(start, -startDay);
    let week = 0;
    while (windows.length < count) {
      const weekMonday = addDays(anchorMonday, week * 7);
      for (let i = 0; i < perWeek; i++) {
        const open = addDays(weekMonday, i * durationDays);
        if (open.getTime() < start.getTime()) continue;
        windows.push({ start: open, deadline: addDays(open, durationDays) });
        if (windows.length === count) break;
      }
      week += 1;
    }
  } else {
    let open = start;
    while (windows.length < count) {
      const deadline = addDays(open, durationDays);
      windows.push({ start: open, deadline });
      open = deadline;
    }
  }
  return windows;
}

/**
 * Map assignment id -> global work-package number (1..N), matching the backend
 * `work_package_numbers`: the chronological rank of each window.
 */
export function workPackageNumbers(assignments: Assignment[]): Map<string, number> {
  const map = new Map<string, number>();
  const ordered = [...assignments].sort(
    (a, b) =>
      new Date(a.start_date).getTime() - new Date(b.start_date).getTime() ||
      new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
  );
  ordered.forEach((a, i) => {
    map.set(a.id, i + 1);
  });
  return map;
}
